import styled from "styled-components";
import { Link } from "react-router-dom";
import { duration, easing, shadows, spacing, typographyMixin } from "@magicbi/ui-library";
import { Tooltip } from "../../atoms/Tooltip";
import { SoonBadge } from "../../atoms/SoonBadge";
import { drawerSlideMotion, scrimMotion } from "../../../design-system/tokens/overlayMotion";
import { mediaQueries } from "../../../design-system/tokens/breakpoints";
import { touchTargetMin } from "../../../styles/mobileLayout";
import { NAV_DRAWER_WIDTH, NAV_PEEK_MS, NAV_PIN_MS, NAV_ROW_HEIGHT, NAV_ROW_RADIUS } from "./NavDrawer.constants";

/** Sits in a positioned parent that starts under the top bar; covers the viewport width. */
export const Scrim = styled.div<{ $closing?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100vw;
  background: ${({ theme }) => theme.colors.surface.overlay};
  ${({ $closing }) => scrimMotion($closing)}
`;

export const Drawer = styled.nav`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  /* GAP: design 288px; nearest token (spacing 72 = 18rem). */
  width: ${NAV_DRAWER_WIDTH};
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};
  box-sizing: border-box;
  padding: ${spacing[3.5]} ${spacing[3]};
  overflow-x: hidden;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${shadows.component.modal};
`;

/** The modal drawer: slides in from the left edge and back out. */
export const ModalDrawer = styled(Drawer)<{ $closing?: boolean }>`
  ${({ $closing }) => drawerSlideMotion($closing)}
`;

/** The always-mounted hover-preview / pinned panel. It never moves on pin; only the page resizes. */
export const PeekDrawer = styled(Drawer)<{ $open: boolean; $docked: boolean }>`
  transform: ${({ $open }) => ($open ? "none" : "translateX(-100%)")};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  box-shadow: ${({ $open, $docked }) => ($open && !$docked ? shadows.component.modal : "none")};
  border-right: 1px solid ${({ theme, $docked }) => ($docked ? theme.colors.border.stroke : "transparent")};
  transition:
    transform ${({ $docked }) => ($docked ? NAV_PIN_MS : NAV_PEEK_MS)}ms ${easing.slideIn},
    box-shadow ${NAV_PIN_MS}ms ${easing.slideIn},
    border-color ${NAV_PIN_MS}ms ${easing.slideIn},
    visibility 0s linear ${({ $open }) => ($open ? "0s" : `${NAV_PIN_MS}ms`)};
  @media (prefers-reduced-motion: reduce) {
    transition-duration: ${duration.instant};
    transition-delay: 0s;
  }
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing[1]} ${spacing[2]} ${spacing[3]};
`;

export const PanelLabel = styled.h2`
  margin: 0;
  ${typographyMixin("body", 4)}
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

export const PinButton = styled.button<{ $pinned: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  border: 0;
  border-radius: ${spacing[2]};
  background: none;
  cursor: pointer;
  color: ${({ theme, $pinned }) => ($pinned ? theme.colors.text.interactive : theme.colors.text.secondary)};
  transition: background-color ${duration.fast} ${easing.slideIn}, color ${duration.fast} ${easing.slideIn};

  svg {
    transition: transform ${NAV_PIN_MS}ms ${easing.slideIn};
    transform: ${({ $pinned }) => ($pinned ? "rotate(45deg)" : "none")};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.surface.detailField};
    color: ${({ theme }) => theme.colors.text.interactive};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary[500]};
    outline-offset: -2px;
  }
  @media (prefers-reduced-motion: reduce) {
    &, svg {
      transition-duration: ${duration.instant};
    }
  }
`;

export const Heading = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const rowStyles = `
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-height: ${NAV_ROW_HEIGHT};
  padding: 0 ${spacing[3]};
  border-radius: ${NAV_ROW_RADIUS};
  text-decoration: none;
  /* Phone: 44px touch target. */
  ${mediaQueries.down.sm} {
    min-height: ${touchTargetMin};
  }
`;

export const ItemLink = styled(Link)<{ $selected: boolean }>`
  ${rowStyles}
  ${typographyMixin("body", 4)}
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  color: ${({ theme, $selected }) => ($selected ? theme.colors.text.interactive : theme.colors.text.primary)};
  background: ${({ theme, $selected }) => ($selected ? theme.colors.surface.detailField : "transparent")};

  &:hover {
    background: ${({ theme }) => theme.colors.surface.detailField};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary[500]};
    outline-offset: -2px;
  }
`;

export const DisabledItem = styled.div`
  ${rowStyles}
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: not-allowed;
  width: 100%;
  box-sizing: border-box;
`;

/** The row wrapper for a disabled item: the Tooltip would otherwise centre the row instead of filling the drawer. */
export const RowTooltip = styled(Tooltip)`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
`;

/** The peach Coming-soon badge, pushed to the row end. */
export const Soon = styled(SoonBadge)`
  margin-left: auto;
`;

export const Divider = styled.hr`
  align-self: stretch;
  height: 1px;
  margin: ${spacing[2]} ${spacing[1]};
  border: 0;
  background: ${({ theme }) => theme.colors.border.stroke};
`;

export const Footer = styled.div`
  margin-top: auto;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: ${spacing[3]};
`;
